import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { PackagePlus, Plus, Edit2, Trash2, Package, AlertTriangle, Download, FileText, FileSpreadsheet, Loader2, Settings, ClipboardList, Minus, Save } from 'lucide-react';
import { ZusatzReportingPanel } from '../../components/ZusatzReportingPanel';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export const ZusatzAdmin = () => {
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [reportingProducts, setReportingProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showRestockDialog, setShowRestockDialog] = useState(false);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [locations, setLocations] = useState([]);   // E4: Standorte des Kunden

  // Stock reporting state
  const [newStocks, setNewStocks] = useState({});
  const [originalStocks, setOriginalStocks] = useState({});
  const [saving, setSaving] = useState(false);

  const today = new Date();
  const firstOfMonth = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
  const lastOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
  const [exportForm, setExportForm] = useState({
    start_date: firstOfMonth,
    end_date: lastOfMonth,
    format: 'pdf',
    location_id: '',
    type: 'summary',   // 'summary' = Zusammenfassung, 'verlauf' = Übergabe-Chronologie
  });
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    stock: '',
    warning_threshold: '5',
    enabled_for_reporting: false,
    location_id: '',
  });

  useEffect(() => { fetchProducts(); fetchLocations(); }, []);

  const fetchLocations = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/user/locations`, { headers: { Authorization: `Bearer ${token}` } });
      setLocations(res.data || []);
    } catch { /* optional */ }
  };

  const locName = (id) => locations.find(l => l.id === id)?.name;

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('token');
      const [allRes, reportingRes] = await Promise.all([
        axios.get(`${API}/api/zusatz/products`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/zusatz/products/reporting`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setProducts(allRes.data);
      const rProds = reportingRes.data || [];
      setReportingProducts(rProds);
      const initStocks = {};
      const origStocks = {};
      rProds.forEach(p => { initStocks[p.id] = p.stock; origStocks[p.id] = p.stock; });
      setNewStocks(initStocks);
      setOriginalStocks(origStocks);
    } catch (error) {
      toast.error('Fehler beim Laden der Produkte');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.name || !formData.price || !formData.stock) {
      toast.error('Bitte alle Pflichtfelder ausfüllen');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/zusatz/products`, {
        name: formData.name,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
        warning_threshold: parseInt(formData.warning_threshold),
        enabled_for_reporting: formData.enabled_for_reporting,
        location_id: formData.location_id || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Produkt erfolgreich erstellt');
      setShowCreateDialog(false);
      setFormData({ name: '', price: '', stock: '', warning_threshold: '5', enabled_for_reporting: false, location_id: '' });
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Erstellen');
    }
  };

  const handleUpdate = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/api/zusatz/products/${selectedProduct.id}`, {
        name: formData.name,
        price: parseFloat(formData.price),
        stock: parseInt(formData.stock),
        warning_threshold: parseInt(formData.warning_threshold),
        enabled_for_reporting: formData.enabled_for_reporting,
        location_id: formData.location_id || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Produkt aktualisiert');
      setShowEditDialog(false);
      setSelectedProduct(null);
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Aktualisieren');
    }
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Produkt wirklich löschen?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/api/zusatz/products/${productId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Produkt gelöscht');
      fetchProducts();
    } catch (error) {
      toast.error('Fehler beim Löschen');
    }
  };

  const handleRestock = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/api/zusatz/products/${selectedProduct.id}/restock`, {
        added_stock: parseInt(formData.stock),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Nachschub erfolgreich hinzugefügt');
      setShowRestockDialog(false);
      setSelectedProduct(null);
      setFormData({ name: '', price: '', stock: '', warning_threshold: '5', enabled_for_reporting: false });
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Nachschub');
    }
  };

  const openEditDialog = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name,
      price: product.price.toString(),
      stock: product.stock.toString(),
      warning_threshold: product.warning_threshold.toString(),
      enabled_for_reporting: product.enabled_for_reporting || false,
      location_id: product.location_id || '',
    });
    setShowEditDialog(true);
  };

  const openRestockDialog = (product) => {
    setSelectedProduct(product);
    setFormData({ name: product.name, price: product.price.toString(), stock: '', warning_threshold: product.warning_threshold.toString() });
    setShowRestockDialog(true);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({
        format: exportForm.format,
        start_date: exportForm.start_date,
        end_date: exportForm.end_date,
      });
      if (exportForm.location_id) params.append('location_id', exportForm.location_id);

      // Wähle den richtigen Endpoint basierend auf Export-Typ
      const endpoint = exportForm.type === 'verlauf'
        ? `${API}/api/zusatz/export/verlauf?${params.toString()}`
        : `${API}/api/zusatz/export?${params.toString()}`;

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });

      const mimeType = exportForm.format === 'pdf' ? 'application/pdf' : 'text/csv';
      const ext = exportForm.format === 'pdf' ? 'pdf' : 'csv';
      const prefix = exportForm.type === 'verlauf' ? 'zusatz_verlauf' : 'zusatz_bericht';
      const filename = `${prefix}_${exportForm.start_date}_${exportForm.end_date}.${ext}`;
      const url = URL.createObjectURL(new Blob([response.data], { type: mimeType }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);

      toast.success(`${exportForm.format.toUpperCase()} erfolgreich heruntergeladen`);
      setShowExportDialog(false);
    } catch (err) {
      toast.error('Fehler beim Export. Bitte erneut versuchen.');
    } finally {
      setExporting(false);
    }
  };

  const getStockStatus = (product) => {
    const stock = product.stock;
    const threshold = product.warning_threshold;
    if (stock === 0) return { color: 'bg-gray-500', text: 'Ausverkauft', icon: '⚫' };
    if (stock < threshold) return { color: 'bg-red-500', text: 'Kritisch', icon: '🔴' };
    if (stock === threshold) return { color: 'bg-yellow-500', text: 'Niedrig', icon: '🟡' };
    return { color: 'bg-green-500', text: 'Gut', icon: '🟢' };
  };

  // ─── Stock reporting helpers ───
  const handleIncrement = (pid) => {
    const max = originalStocks[pid] || 0;
    setNewStocks(prev => ({ ...prev, [pid]: Math.min(max, (prev[pid] || 0) + 1) }));
  };
  const handleDecrement = (pid) => {
    setNewStocks(prev => ({ ...prev, [pid]: Math.max(0, (prev[pid] || 0) - 1) }));
  };
  const handleIncrement5 = (pid) => {
    const max = originalStocks[pid] || 0;
    setNewStocks(prev => ({ ...prev, [pid]: Math.min(max, (prev[pid] || 0) + 5) }));
  };
  const handleDecrement5 = (pid) => {
    setNewStocks(prev => ({ ...prev, [pid]: Math.max(0, (prev[pid] || 0) - 5) }));
  };
  const getDifference = (pid) => {
    const product = reportingProducts.find(p => p.id === pid);
    return product ? (newStocks[pid] || 0) - product.stock : 0;
  };
  const hasChanges = () => {
    return Object.entries(newStocks).some(([pid, ns]) => {
      const p = reportingProducts.find(pr => pr.id === pid);
      return p && ns !== p.stock;
    });
  };
  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const promises = Object.entries(newStocks).map(([pid, ns]) =>
        axios.post(`${API}/api/zusatz/products/${pid}/report-stock`, { new_stock: ns }, { headers: { Authorization: `Bearer ${token}` } })
      );
      await Promise.all(promises);
      const changedCount = Object.entries(newStocks).filter(([pid, ns]) => {
        const p = reportingProducts.find(pr => pr.id === pid);
        return p && ns !== p.stock;
      }).length;
      toast.success(changedCount > 0 ? `${changedCount} Bestand(e) aktualisiert` : 'Bestand bestätigt (keine Verkäufe)');
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };
  const getReportStockStatus = (product) => {
    const cs = newStocks[product.id] || 0;
    const t = product.warning_threshold;
    if (cs === 0) return { icon: '⚫' };
    if (cs < t) return { icon: '🔴' };
    if (cs === t) return { icon: '🟡' };
    return { icon: '🟢' };
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-600 dark:text-gray-400">Lade...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="zusatz-admin-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100">Zusatzprodukte</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">Verwalten Sie Ihre Zusatzprodukte</p>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button
              data-testid="zusatz-export-btn"
              variant="outline"
              onClick={() => setShowExportDialog(true)}
              className="flex-1 sm:flex-none border-[#094199] text-[#094199] hover:bg-[#e8f0fe]"
            >
              <Download className="h-4 w-4 mr-2" />
              Export
            </Button>
            {activeTab === 'products' && (
              <Button
                data-testid="zusatz-create-btn"
                onClick={() => setShowCreateDialog(true)}
                className="flex-1 sm:flex-none"
              >
                <Plus className="h-4 w-4 mr-2" />
                Neues Produkt
              </Button>
            )}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('products')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'products' ? 'border-[#094199] text-[#094199]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid="zusatz-tab-products"
          >
            <Settings className="h-4 w-4" />
            Produktverwaltung
          </button>
          <button
            onClick={() => setActiveTab('report')}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'report' ? 'border-[#094199] text-[#094199]' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            data-testid="zusatz-tab-report"
          >
            <ClipboardList className="h-4 w-4" />
            Bestand melden
          </button>
        </div>

        {/* Tab: Produktverwaltung */}
        {activeTab === 'products' && (
          <>
        {/* Products List */}
        {products.length === 0 ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-16">
              <PackagePlus className="h-12 w-12 text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400 text-center">Noch keine Produkte vorhanden</p>
              <Button onClick={() => setShowCreateDialog(true)} className="mt-4" data-testid="zusatz-first-create">
                Erstes Produkt anlegen
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((product) => {
              const status = getStockStatus(product);
              return (
                <Card key={product.id} className={`${status.color === 'bg-red-500' || status.color === 'bg-gray-500' ? 'border-2 border-red-400 dark:border-red-600' : ''}`} data-testid={`zusatz-product-${product.id}`}>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        {status.icon} {product.name}
                      </span>
                      <span className="text-lg font-bold text-green-600 dark:text-green-400">{product.price.toFixed(2)} EUR</span>
                    </CardTitle>
                    {locations.length > 1 && (
                      <Badge variant="outline" className="w-fit mt-1 text-[10px] font-normal" data-testid={`zusatz-product-location-${product.id}`}>
                        {product.location_id ? (locName(product.location_id) || 'Standort') : 'Alle Standorte'}
                      </Badge>
                    )}
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className={`p-3 rounded-lg ${status.color === 'bg-red-500' || status.color === 'bg-gray-500' ? 'bg-red-50 dark:bg-red-900/30' : status.color === 'bg-yellow-500' ? 'bg-yellow-50 dark:bg-yellow-900/30' : 'bg-green-50 dark:bg-green-900/30'}`}>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Bestand: {product.stock} Stück</p>
                      <p className="text-xs text-gray-600 dark:text-gray-400">Warnschwelle: {product.warning_threshold} Stück</p>
                      {(product.stock <= product.warning_threshold && product.stock > 0) && (
                        <div className="flex items-center gap-1 mt-1 text-yellow-700 dark:text-yellow-400">
                          <AlertTriangle className="h-3 w-3" />
                          <span className="text-xs font-medium">NIEDRIG!</span>
                        </div>
                      )}
                      {product.stock === 0 && (
                        <div className="flex items-center gap-1 mt-1 text-red-700 dark:text-red-400">
                          <AlertTriangle className="h-3 w-3" />
                          <span className="text-xs font-medium">AUSVERKAUFT!</span>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button variant="outline" size="sm" onClick={() => openEditDialog(product)} className="flex-1" data-testid={`zusatz-edit-${product.id}`}>
                        <Edit2 className="h-3 w-3 mr-1" />
                        Bearbeiten
                      </Button>
                      {product.stock === 0 && (
                        <Button size="sm" onClick={() => openRestockDialog(product)} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid={`zusatz-restock-${product.id}`}>
                          <Package className="h-3 w-3 mr-1" />
                          Nachschub
                        </Button>
                      )}
                      <Button variant="outline" size="sm" onClick={() => handleDelete(product.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30" data-testid={`zusatz-delete-${product.id}`}>
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
          </>
        )}

        {/* Tab: Bestand melden — nutzt neue geschützte Übergabe-Logik (Task #104) */}
        {activeTab === 'report' && (
          <ZusatzReportingPanel />
        )}

        {/* Create Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-md" aria-describedby="create-zusatz-desc">
            <DialogHeader>
              <DialogTitle>Neues Zusatzprodukt anlegen</DialogTitle>
              <p id="create-zusatz-desc" className="text-sm text-muted-foreground">Produkt mit Name, Preis und Bestand anlegen</p>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Name *</Label>
                <Input id="name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="z.B. Werbeartikel XY" data-testid="zusatz-input-name" />
              </div>
              <div>
                <Label htmlFor="price">Preis (EUR) *</Label>
                <Input id="price" type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} placeholder="5.00" data-testid="zusatz-input-price" />
              </div>
              <div>
                <Label htmlFor="stock">Bestand (Stück) *</Label>
                <Input id="stock" type="number" value={formData.stock} onChange={(e) => setFormData({ ...formData, stock: e.target.value })} placeholder="20" data-testid="zusatz-input-stock" />
              </div>
              <div>
                <Label htmlFor="threshold">Warnschwelle (Stück) *</Label>
                <Input id="threshold" type="number" value={formData.warning_threshold} onChange={(e) => setFormData({ ...formData, warning_threshold: e.target.value })} placeholder="5" data-testid="zusatz-input-threshold" />
              </div>
              {locations.length > 1 && (
                <div>
                  <Label htmlFor="create-location">Standort</Label>
                  <select id="create-location" value={formData.location_id}
                    onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                    className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    data-testid="zusatz-input-location">
                    <option value="">Alle Standorte</option>
                    {locations.map(l => (<option key={l.id} value={l.id}>{l.name}</option>))}
                  </select>
                </div>
              )}
              <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700">
                <input type="checkbox" checked={formData.enabled_for_reporting} onChange={(e) => setFormData({ ...formData, enabled_for_reporting: e.target.checked })} className="h-4 w-4 rounded border-gray-300" data-testid="zusatz-input-reporting" />
                <div>
                  <span className="text-sm font-medium">MA-Freigabe (Bestandsmeldung)</span>
                  <p className="text-xs text-muted-foreground">Mitarbeiter können den Bestand dieses Produkts melden</p>
                </div>
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Abbrechen</Button>
              <Button onClick={handleCreate} data-testid="zusatz-save-btn">Speichern</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-md" aria-describedby="edit-zusatz-desc">
            <DialogHeader>
              <DialogTitle>Produkt bearbeiten</DialogTitle>
              <p id="edit-zusatz-desc" className="text-sm text-muted-foreground">Produktdaten anpassen</p>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="edit-name">Name *</Label>
                <Input id="edit-name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="edit-price">Preis (EUR) *</Label>
                <Input id="edit-price" type="number" step="0.01" value={formData.price} onChange={(e) => setFormData({ ...formData, price: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="edit-stock">Bestand (Stück) *</Label>
                <Input id="edit-stock" type="number" value={formData.stock} onChange={(e) => setFormData({ ...formData, stock: e.target.value })} />
              </div>
              <div>
                <Label htmlFor="edit-threshold">Warnschwelle (Stück) *</Label>
                <Input id="edit-threshold" type="number" value={formData.warning_threshold} onChange={(e) => setFormData({ ...formData, warning_threshold: e.target.value })} />
              </div>
              {locations.length > 1 && (
                <div>
                  <Label htmlFor="edit-location">Standort</Label>
                  <select id="edit-location" value={formData.location_id}
                    onChange={(e) => setFormData({ ...formData, location_id: e.target.value })}
                    className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    data-testid="zusatz-edit-location">
                    <option value="">Alle Standorte</option>
                    {locations.map(l => (<option key={l.id} value={l.id}>{l.name}</option>))}
                  </select>
                </div>
              )}
              <label className="flex items-center gap-2 cursor-pointer p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-slate-700">
                <input type="checkbox" checked={formData.enabled_for_reporting} onChange={(e) => setFormData({ ...formData, enabled_for_reporting: e.target.checked })} className="h-4 w-4 rounded border-gray-300" />
                <div>
                  <span className="text-sm font-medium">MA-Freigabe (Bestandsmeldung)</span>
                  <p className="text-xs text-muted-foreground">Mitarbeiter können den Bestand melden</p>
                </div>
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEditDialog(false)}>Abbrechen</Button>
              <Button onClick={handleUpdate}>Aktualisieren</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Restock Dialog */}
        <Dialog open={showRestockDialog} onOpenChange={setShowRestockDialog}>
          <DialogContent className="max-w-md" aria-describedby="restock-zusatz-desc">
            <DialogHeader>
              <DialogTitle>Nachschub: {selectedProduct?.name}</DialogTitle>
              <p id="restock-zusatz-desc" className="text-sm text-muted-foreground">Bestand auffüllen</p>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-gray-50 dark:bg-slate-800 p-3 rounded-lg">
                <p className="text-sm text-gray-600 dark:text-gray-400">Aktueller Bestand:</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{selectedProduct?.stock} Stück</p>
              </div>
              <div>
                <Label htmlFor="restock-qty">Neue Lieferung (Stück) *</Label>
                <Input id="restock-qty" type="number" value={formData.stock} onChange={(e) => setFormData({ ...formData, stock: e.target.value })} placeholder="20" data-testid="zusatz-restock-qty" />
              </div>
              {formData.stock && (
                <div className="bg-green-50 dark:bg-green-900/30 p-3 rounded-lg">
                  <p className="text-sm text-gray-600 dark:text-gray-400">Neuer Bestand:</p>
                  <p className="text-2xl font-bold text-green-700 dark:text-green-400">{(selectedProduct?.stock || 0) + parseInt(formData.stock || 0)} Stück</p>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRestockDialog(false)}>Abbrechen</Button>
              <Button onClick={handleRestock} className="bg-blue-600 hover:bg-blue-700">Hinzufügen</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Export Dialog */}
        <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
          <DialogContent className="max-w-md" aria-describedby="export-zusatz-desc">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Download className="h-5 w-5 text-[#094199]" />
                Zusatzprodukte Verkaufsbericht exportieren
              </DialogTitle>
              <p id="export-zusatz-desc" className="text-sm text-muted-foreground">Zeitraum und Format wählen</p>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="export-start">Von</Label>
                  <Input id="export-start" type="date" value={exportForm.start_date} onChange={(e) => setExportForm({ ...exportForm, start_date: e.target.value })} data-testid="zusatz-export-start" />
                </div>
                <div>
                  <Label htmlFor="export-end">Bis</Label>
                  <Input id="export-end" type="date" value={exportForm.end_date} onChange={(e) => setExportForm({ ...exportForm, end_date: e.target.value })} data-testid="zusatz-export-end" />
                </div>
              </div>
              <div>
                <Label>Export-Typ</Label>
                <div className="flex gap-3 mt-2">
                  <button onClick={() => setExportForm({ ...exportForm, type: 'summary' })}
                    className={`flex-1 flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-colors text-sm font-medium ${exportForm.type === 'summary' ? 'border-[#094199] bg-[#e8f0fe] dark:bg-[#094199]/30 text-[#094199]' : 'border-border text-muted-foreground hover:border-[#094199]'}`}
                    data-testid="zusatz-export-summary">
                    <FileText className="h-4 w-4" />
                    <span>Zusammenfassung</span>
                    <span className="text-[10px] font-normal opacity-70">Pro Produkt</span>
                  </button>
                  <button onClick={() => setExportForm({ ...exportForm, type: 'verlauf' })}
                    className={`flex-1 flex flex-col items-center gap-1 p-3 rounded-xl border-2 transition-colors text-sm font-medium ${exportForm.type === 'verlauf' ? 'border-[#094199] bg-[#e8f0fe] dark:bg-[#094199]/30 text-[#094199]' : 'border-border text-muted-foreground hover:border-[#094199]'}`}
                    data-testid="zusatz-export-verlauf">
                    <FileSpreadsheet className="h-4 w-4" />
                    <span>Übergabe-Verlauf</span>
                    <span className="text-[10px] font-normal opacity-70">Alle Übergaben</span>
                  </button>
                </div>
              </div>
              <div>
                <Label>Format</Label>
                <div className="flex gap-3 mt-2">
                  <button onClick={() => setExportForm({ ...exportForm, format: 'pdf' })} className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-colors text-sm font-medium ${exportForm.format === 'pdf' ? 'border-[#094199] bg-[#e8f0fe] dark:bg-[#094199]/30 text-[#094199] dark:text-blue-300' : 'border-border text-muted-foreground hover:border-[#094199]'}`} data-testid="zusatz-export-pdf">
                    <FileText className="h-4 w-4" /> PDF
                  </button>
                  <button onClick={() => setExportForm({ ...exportForm, format: 'csv' })} className={`flex-1 flex items-center justify-center gap-2 p-3 rounded-xl border-2 transition-colors text-sm font-medium ${exportForm.format === 'csv' ? 'border-[#094199] bg-[#e8f0fe] dark:bg-[#094199]/30 text-[#094199] dark:text-blue-300' : 'border-border text-muted-foreground hover:border-[#094199]'}`} data-testid="zusatz-export-csv">
                    <FileSpreadsheet className="h-4 w-4" /> CSV
                  </button>
                </div>
              </div>
              {locations.length > 1 && (
                <div>
                  <Label htmlFor="export-location">Standort</Label>
                  <select id="export-location" value={exportForm.location_id}
                    onChange={(e) => setExportForm({ ...exportForm, location_id: e.target.value })}
                    className="mt-1 w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                    data-testid="zusatz-export-location">
                    <option value="">Alle Standorte</option>
                    {locations.map(l => (<option key={l.id} value={l.id}>{l.name}</option>))}
                  </select>
                </div>
              )}
              <div className="text-xs text-muted-foreground bg-muted rounded-lg p-3">
                <strong>Zusammenfassung:</strong> Produkt-Überblick mit Gesamtbeträgen.<br/>
                <strong>Übergabe-Verlauf:</strong> Jede Schicht-Übergabe einzeln mit Datum, Betrag, Status.
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowExportDialog(false)}>Abbrechen</Button>
              <Button onClick={handleExport} disabled={exporting} className="bg-[#094199] hover:bg-[#0a3580]" data-testid="zusatz-export-download">
                {exporting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Erstelle...</> : <><Download className="h-4 w-4 mr-2" /> Herunterladen</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};
